/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React from 'react'
import { NavLink } from 'react-router-dom'
import cx from 'classnames'

import { EuiFlexGroup, EuiFlexItem, EuiButtonIcon } from '@elastic/eui'

import type { Interpolation, Theme } from '@emotion/react'
import type { CSSProperties, ReactElement, ReactNode } from 'react'

interface NavLinkItemProps {
  href: string
  children: ReactNode
  className?: string
  exact?: boolean
  beta?: boolean
  'data-test-id'?: string
  iconRight?: ReactElement
  hasCaret?: boolean
  itemIsOpen?: boolean
  onCaretClick?: () => void
  css?: Interpolation<Theme>
}

const NavLinkItem = ({
  href,
  className,
  children,
  exact = false,
  iconRight,
  hasCaret,
  itemIsOpen,
  'data-test-id': dataTestId,
  onCaretClick,
  css,
}: NavLinkItemProps) => {
  const classes = ({ isActive }) =>
    cx(className, {
      'euiSideNavItemButton-isSelected': isActive,
    })

  const link = (
    <NavLink
      to={href}
      end={exact}
      style={css as CSSProperties}
      className={classes}
      data-test-id={dataTestId}
    >
      {children}
    </NavLink>
  )

  if (hasCaret || iconRight) {
    return (
      <EuiFlexGroup
        responsive={false}
        gutterSize='xs'
        alignItems='center'
        justifyContent='spaceBetween'
        data-test-id={`${dataTestId}-flex-group`}
      >
        <EuiFlexItem grow={false}>{link}</EuiFlexItem>
        {iconRight && <EuiFlexItem grow={false}>{iconRight}</EuiFlexItem>}
        {hasCaret && (
          <EuiFlexItem grow={false}>
            <EuiButtonIcon
              aria-label={itemIsOpen ? 'Hide sub-items' : 'Show sub-items'}
              iconType={itemIsOpen ? 'arrowDown' : 'arrowRight'}
              size='xs'
              onClick={onCaretClick}
            />
          </EuiFlexItem>
        )}
      </EuiFlexGroup>
    )
  }

  return link
}

export default NavLinkItem
